import { useEffect, useState } from "react"

export type ImagePadding = { left: number; right: number; top: number; bottom: number }

export function useImagePadding(src: string | undefined) {
    const [padding, setPadding] = useState<ImagePadding | undefined>(undefined)

    useEffect(() => {
        setPadding(undefined)
        if (!src) return

        let cancelled = false
        const img = new Image()
        img.crossOrigin = "anonymous"
        img.onerror = () => {}
        img.onload = () => {
            if (cancelled) return
            try {
                const scale = Math.min(1, 200 / img.naturalWidth)
                const w = Math.floor(img.naturalWidth * scale)
                const h = Math.floor(img.naturalHeight * scale)
                const canvas = document.createElement("canvas")
                canvas.width = w
                canvas.height = h
                const ctx = canvas.getContext("2d")
                if (!ctx) return
                ctx.drawImage(img, 0, 0, w, h)
                const { data } = ctx.getImageData(0, 0, w, h)

                const hasPixel = (x: number, y: number) => data[(y * w + x) * 4 + 3] > 10

                let left = 0
                outer: for (let x = 0; x < w; x++) {
                    for (let y = 0; y < h; y++) {
                        if (hasPixel(x, y)) {
                            left = x / w
                            break outer
                        }
                    }
                }

                let right = 0
                outer: for (let x = w - 1; x >= 0; x--) {
                    for (let y = 0; y < h; y++) {
                        if (hasPixel(x, y)) {
                            right = (w - 1 - x) / w
                            break outer
                        }
                    }
                }

                let top = 0
                outer: for (let y = 0; y < h; y++) {
                    for (let x = 0; x < w; x++) {
                        if (hasPixel(x, y)) {
                            top = y / h
                            break outer
                        }
                    }
                }

                let bottom = 0
                outer: for (let y = h - 1; y >= 0; y--) {
                    for (let x = 0; x < w; x++) {
                        if (hasPixel(x, y)) {
                            bottom = (h - 1 - y) / h
                            break outer
                        }
                    }
                }

                setPadding({ left, right, top, bottom })
            } catch {
                setPadding({ left: 0, right: 0, top: 0, bottom: 0 })
            }
        }
        img.src = src

        return () => {
            cancelled = true
        }
    }, [src])

    return padding
}
